import { TimeValue } from './TimeValue';

/**
 * Centralized function for preparing time data to send to API
 * 
 * @param {string|TimeValue} timeValue - The time value to prepare
 * @returns {string} UTC time string ready for API
 */
export function prepareTimeForAPI(timeValue) {
  // Create a TimeValue if not already one
  const timeObj = TimeValue.from(timeValue);
  
  // Ensure we're sending UTC to API
  const utcTime = timeObj.toUTC();
  
  // Log the conversion for debugging
  console.log(`🔄 API Time Preparation: ${timeObj.value} (${timeObj.format}) → ${utcTime.value} (UTC)`);
  
  // Return only the string value for the API
  return utcTime.value;
}

/**
 * Function for handling time data received from API
 * 
 * @param {string} timeString - Time string from API (always UTC)
 * @returns {TimeValue} TimeValue object with UTC format
 */
export function processTimeFromAPI(timeString) {
  // API always sends UTC, so create a UTC TimeValue
  const timeValue = new TimeValue(timeString, 'UTC');
  console.log(`📥 Received API time: ${timeString} (UTC)`);
  return timeValue;
}

/**
 * Format a TimeValue for display in the UI
 * 
 * @param {string|TimeValue} timeValue - Time value to format
 * @param {boolean} as12Hour - Whether to format as 12-hour time
 * @returns {string} Formatted time string
 */
export function formatTimeForDisplay(timeValue, as12Hour = true) {
  // Create a TimeValue if not already one
  const timeObj = TimeValue.from(timeValue);
  
  // Ensure time is in LOCAL format for display
  const localTime = timeObj.format === 'UTC' ? timeObj.toLocal() : timeObj;
  
  if (!localTime.value) return '';
  
  // Use standard format (or implement 12-hour formatting)
  if (!as12Hour) {
    return localTime.value.substring(0, 5); // Return HH:MM
  }
  
  // Format as 12-hour time
  try {
    const timeParts = localTime.value.split(':');
    const hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    console.error('Error formatting time for display:', error);
    return localTime.value;
  }
} 